import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import IncludeSideBar from "../../Components/Sidebar/IncludeSideBar";
import useDebounce from "../../utils/useDebounce";
import "./UserAnalyticsList.css";

const UserAnalyticsList = () => {
  const allUserApis = require("../../apis/users");
  const navigate = useNavigate();
  
  const [userData, setUserData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState("");
  const [sortOrder, setSortOrder] = useState("DESC");

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const getUsers = async (
    page,
    searchTerm = "",
    sortBy = "",
    sortOrder = "DESC"
  ) => {
    setLoading(true);
    try {
      const filt = { page, searchTerm, sortBy, sortOrder };
      const response = await allUserApis.getAllUsers(filt);
      setUserData(response.users);
      setTotalPages(response.pagination.totalPages);
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUsers(currentPage, "", sortBy, sortOrder); // Initial load without search
  }, [currentPage, sortBy, sortOrder]);

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handleSearchSubmit = () => {
    getUsers(currentPage, searchTerm, sortBy, sortOrder);
  };

  const handleUserClick = (userId) => {
    navigate(`/user-analytics/${userId}`);
  };

  const handleSort = (field) => {
    const newSortOrder = sortBy === field && sortOrder === "DESC" ? "ASC" : "DESC";
    setSortBy(field);
    setSortOrder(newSortOrder);
  };

  const handlePageClick = (page) => {
    setCurrentPage(page);
  };

  const handleFirst = () => {
    setCurrentPage(1);
  };

  const handleLast = () => {
    setCurrentPage(totalPages);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getStatusBadge = (active) => {
    return active === 1 ? (
      <span className="status-badge active">Active</span>
    ) : (
      <span className="status-badge inactive">Inactive</span>
    );
  };

  const getAccountTypeBadge = (account_type) => {
    const className = `account-type-badge ${account_type?.toLowerCase()}`;
    return <span className={className}>{account_type || "Basic"}</span>;
  };

  return (
    <IncludeSideBar>
      <div className="user-analytics-container">
        <div className="header-section">
          <h2>User Analytics</h2>
          <p>Click on any user to view their detailed analytics</p>
        </div>

        <div className="controls-section">
          <div className="search-container">
            <input
              type="text"
              placeholder="Search users by name, email, or username..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="search-input"
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleSearchSubmit();
                }
              }}
            />
            <button className="search-btn" onClick={handleSearchSubmit}>
              Search
            </button>
          </div>
        </div>

        <div className="table-container">
          {loading ? (
            <div className="loading">Loading users...</div>
          ) : (
            <table className="users-table">
              <thead>
                <tr>
                  <th onClick={() => handleSort("id")}>
                    ID {sortBy === "id" && (sortOrder === "DESC" ? "↓" : "↑")}
                  </th>
                  <th onClick={() => handleSort("first_name")}>
                    Name{" "}
                    {sortBy === "first_name" &&
                      (sortOrder === "DESC" ? "↓" : "↑")}
                  </th>
                  <th onClick={() => handleSort("username")}>
                    Username{" "}
                    {sortBy === "username" &&
                      (sortOrder === "DESC" ? "↓" : "↑")}
                  </th>
                  <th onClick={() => handleSort("email")}>
                    Email{" "}
                    {sortBy === "email" && (sortOrder === "DESC" ? "↓" : "↑")}
                  </th>
                  <th onClick={() => handleSort("account_type")}>
                    Account Type{" "}
                    {sortBy === "account_type" &&
                      (sortOrder === "DESC" ? "↓" : "↑")}
                  </th>
                  <th onClick={() => handleSort("active")}>
                    Status{" "}
                    {sortBy === "active" && (sortOrder === "DESC" ? "↓" : "↑")}
                  </th>
                  <th onClick={() => handleSort("createdAt")}>
                    Joined Date{" "}
                    {sortBy === "createdAt" &&
                      (sortOrder === "DESC" ? "↓" : "↑")}
                  </th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {userData.map((user) => (
                  <tr
                    key={user.id}
                    className="user-row"
                    onClick={() => handleUserClick(user.id)}
                  >
                    <td>{user.id}</td>
                    <td>
                      <div className="user-info">
                        {user.profile_pic && (
                          <img
                            src={user.profile_pic}
                            alt={user.first_name}
                            className="user-avatar"
                          />
                        )}
                        <span>
                          {user.first_name} {user.last_name}
                        </span>
                      </div>
                    </td>
                    <td>@{user.username}</td>
                    <td>{user.email}</td>
                    <td>{getAccountTypeBadge(user.account_type)}</td>
                    <td>{getStatusBadge(user.active)}</td>
                    <td>
                      {new Date(user.createdAt).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </td>
                    <td>
                      <button
                        className="view-analytics-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleUserClick(user.id);
                        }}
                      >
                        View Analytics
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button onClick={handleFirst} disabled={currentPage === 1}>
              First
            </button>
            <button onClick={handlePrevious} disabled={currentPage === 1}>
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>
            <button onClick={handleNext} disabled={currentPage === totalPages}>
              Next
            </button>
            <button onClick={handleLast} disabled={currentPage === totalPages}>
              Last
            </button>
          </div>
        )}

        {userData.length === 0 && !loading && (
          <div className="no-users">
            <p>No users found</p>
          </div>
        )}
      </div>
    </IncludeSideBar>
  );
};

export default UserAnalyticsList;
